import { useEffect, useRef, useState } from 'react';
import {
  browserMicrophoneAdapter,
  isMicrophoneError,
  type MicrophoneAdapter,
  type MicrophoneStream,
} from '../data/microphone';
import type { TrainedLabel } from '../data/types';
import { browserAudioTrainer } from '../ml/browserAudioTrainer';
import type { AudioTrainer, LoadedAudioModel } from '../ml/audioTrainer';
import { HelpTooltip } from '../components/HelpTooltip/HelpTooltip';
import { ConfidenceBars } from './ConfidenceBars';
import { LiveSpectrogram } from './LiveSpectrogram';
import './PredictPanel.css';

type Status = 'opening' | 'loading-model' | 'ready' | 'error';

export interface AudioPredictPanelProps {
  projectId: string;
  labels: TrainedLabel[];
  onClose: () => void;
  /** Optional footer slot for the "now what?" actions — same as
   *  PredictPanel. See PredictPanel.tsx for rationale. */
  nextActions?: React.ReactNode;
  /** For tests; production paths use the real adapters. */
  trainer?: AudioTrainer;
  microphone?: MicrophoneAdapter;
}

/**
 * Live audio prediction. Captures a fresh 1-second spectrogram each tick
 * and runs it through the trained CNN — the same shape the model saw at
 * training time. ConfidenceBars handles the OOD-honesty rendering: top
 * confidence below the threshold renders "I'm not sure what this is."
 */
export function AudioPredictPanel({
  projectId,
  labels,
  onClose,
  nextActions,
  trainer = browserAudioTrainer,
  microphone = browserMicrophoneAdapter,
}: AudioPredictPanelProps): JSX.Element {
  const [status, setStatus] = useState<Status>('opening');
  const [error, setError] = useState<string | null>(null);
  const [probabilities, setProbabilities] = useState<number[]>(() =>
    new Array(labels.length).fill(0),
  );
  const [analyser, setAnalyser] = useState<AnalyserNode | null>(null);
  const [audioContext, setAudioContext] = useState<AudioContext | null>(null);
  /** TM-style overlap factor: how much of the previous 1-second window
   *  is reused for the next prediction. 0 = no overlap (predict every
   *  1s). 0.5 = half-overlap (predict every 0.5s). The kid sees more
   *  responsive bars at higher overlap, at the cost of more CPU. */
  const [overlapFactor, setOverlapFactor] = useState(0.5);
  const handleRef = useRef<MicrophoneStream | null>(null);
  const modelRef = useRef<LoadedAudioModel | null>(null);
  const overlapRef = useRef(overlapFactor);
  const rollingRef = useRef<{ pcm: Float32Array; sampleRate: number } | null>(null);
  const inFlight = useRef(false);

  // Keep the live overlap value visible to the long-running predict
  // loop without re-mounting it whenever the slider moves.
  useEffect(() => {
    overlapRef.current = overlapFactor;
  }, [overlapFactor]);

  // 1. Load the trained model once for the lifetime of the panel.
  useEffect(() => {
    let cancelled = false;
    (async (): Promise<void> => {
      setStatus('loading-model');
      try {
        const loaded = await trainer.loadModel(projectId);
        if (cancelled) {
          if (loaded) trainer.disposeModel(loaded);
          return;
        }
        if (!loaded) {
          setError('No trained model was found for this project. Train it first.');
          setStatus('error');
          return;
        }
        modelRef.current = loaded;
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Could not load the trained model.');
        setStatus('error');
      }
    })();
    return () => {
      cancelled = true;
      if (modelRef.current) {
        trainer.disposeModel(modelRef.current);
        modelRef.current = null;
      }
    };
  }, [projectId, trainer]);

  // 2. Open the mic.
  useEffect(() => {
    let cancelled = false;
    (async (): Promise<void> => {
      try {
        const opened = await microphone.open();
        if (cancelled) {
          microphone.close(opened);
          return;
        }
        handleRef.current = opened;
        setAnalyser(opened.analyser);
        setAudioContext(opened.context);
        if (modelRef.current && !cancelled) setStatus('ready');
      } catch (err) {
        if (cancelled) return;
        setError(isMicrophoneError(err) ? err.message : 'Could not turn on the microphone.');
        setStatus('error');
      }
    })();
    return () => {
      cancelled = true;
      setAnalyser(null);
      setAudioContext(null);
      if (handleRef.current) {
        microphone.close(handleRef.current);
        handleRef.current = null;
      }
    };
  }, [microphone]);

  // Promote to ready once both the mic and the model are loaded — the
  // load-order race depends on which finishes first.
  useEffect(() => {
    if (status === 'ready' || status === 'error') return;
    if (modelRef.current && handleRef.current) setStatus('ready');
  }, [status]);

  // 3. Prediction loop. Maintains a rolling 1-second PCM buffer and only
  //    captures (1 - overlap) seconds per tick — at overlap=0.5 the model
  //    sees a fresh prediction every 500ms, with each prediction using
  //    the most-recent 1 second of audio. Same spectrogram pipeline as
  //    training, so the input distribution matches.
  useEffect(() => {
    if (status !== 'ready') return;
    let stopped = false;
    rollingRef.current = null;
    const tick = async (): Promise<void> => {
      if (stopped || inFlight.current) return;
      const handle = handleRef.current;
      const model = modelRef.current;
      if (!handle || !model) return;
      inFlight.current = true;
      try {
        const overlap = Math.max(0, Math.min(0.95, overlapRef.current));
        const newSeconds = Math.max(0.05, 1 - overlap);
        const captured = await microphone.captureWindow(handle, newSeconds);
        if (stopped) return;
        const sampleRate = captured.sampleRate;
        // Splice the new audio onto the rolling buffer; trim to exactly
        // one second so the model always sees a stride consistent with
        // training inputs.
        const oneSecond = sampleRate;
        const prev = rollingRef.current;
        const prevPcm = prev && prev.sampleRate === sampleRate ? prev.pcm : new Float32Array(0);
        const combined = new Float32Array(prevPcm.length + captured.pcm.length);
        combined.set(prevPcm, 0);
        combined.set(captured.pcm, prevPcm.length);
        const start = Math.max(0, combined.length - oneSecond);
        const window = combined.subarray(start);
        const padded =
          window.length === oneSecond
            ? window
            : (() => {
                // First few ticks before the buffer is full — pad with
                // leading silence so the spectrogram pipeline gets a
                // consistent shape.
                const out = new Float32Array(oneSecond);
                out.set(window, oneSecond - window.length);
                return out;
              })();
        rollingRef.current = {
          pcm: window.length === oneSecond ? new Float32Array(window) : padded,
          sampleRate,
        };
        const probs = await trainer.predictAudio(model, padded, sampleRate);
        if (!stopped) setProbabilities(probs);
      } catch {
        // Per-tick errors are swallowed; next tick gets a fresh shot.
      } finally {
        inFlight.current = false;
      }
      if (!stopped) void tick();
    };
    void tick();
    return () => {
      stopped = true;
      rollingRef.current = null;
    };
  }, [status, microphone, trainer]);

  return (
    <section className="bl-predict" aria-label="Live audio prediction">
      <header className="bl-predict__header">
        <h3 className="bl-predict__title">Test the model</h3>
        <button type="button" className="bl-predict__close" onClick={onClose}>
          Close
        </button>
      </header>

      <div className="bl-predict__viewfinder bl-predict__viewfinder--upload">
        {status === 'opening' && (
          <p className="bl-predict__overlay" role="status">
            Turning on the microphone…
          </p>
        )}
        {status === 'loading-model' && (
          <p className="bl-predict__overlay" role="status">
            Loading your model…
          </p>
        )}
        {status === 'ready' && (
          <>
            <p className="bl-predict__overlay" role="status">
              Make a sound. The bars update as it listens.
            </p>
            <LiveSpectrogram
              analyser={analyser}
              context={audioContext}
              highlightWindow
              pixelsPerSecond={60}
              width={360}
              height={96}
            />
          </>
        )}
        {status === 'error' && (
          <p className="bl-predict__overlay bl-predict__overlay--error" role="alert">
            {error}
          </p>
        )}
      </div>

      {status === 'ready' && (
        <label className="bl-predict__overlap" aria-label="Overlap factor">
          <span className="bl-predict__overlap-label">
            Overlap
            <HelpTooltip label="What is overlap?">
              How much each 1-second prediction window shares with the last one. Higher overlap
              means the bars update faster, but the model has to listen more often.
            </HelpTooltip>
          </span>
          <input
            type="range"
            min={0}
            max={0.9}
            step={0.05}
            value={overlapFactor}
            onChange={(e) => setOverlapFactor(Number.parseFloat(e.target.value))}
            aria-valuemin={0}
            aria-valuemax={0.9}
            aria-valuenow={overlapFactor}
          />
          <span className="bl-predict__overlap-value">{overlapFactor.toFixed(2)}</span>
        </label>
      )}

      <ConfidenceBars labels={labels} probabilities={probabilities} />

      {nextActions && (
        <footer className="bl-predict__next" aria-label="What to do with this model">
          <p className="bl-predict__next-h">What&rsquo;s next</p>
          <div className="bl-predict__next-actions">{nextActions}</div>
        </footer>
      )}
    </section>
  );
}
